/* eslint-disable react/jsx-props-no-spreading */
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import CustomModalHeader from 'components/modal/customModalHeader';
import GroupButton from 'components/button/groupButton';
import { toast } from 'react-toastify';
import { messageErrors } from 'utils/utils';
import { Modal } from 'flowbite-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { themModal } from 'utils/theme';
import '../../form/styles.scss';
import InputText from 'components/commonComponent/inputText';
import { formatOptions, integrationOptions, urlRegex } from 'utils/constants';
import MultiSelect from 'components/select/index';
import { updateOrgVendorDetail } from 'api/vendorApi';
import { updateOrgVendorDetailPayload } from 'utils/proptypes';

type vendorModalProps = {
  openModal: boolean,
  setOpenModal: Function,
  headerTitle?: string,
  targetData?: any,
  isEdit: boolean,
  queryClient?: any,
  setCurrentPage?: any,
  currentPage?: number,
};
const VendorModal = (props: vendorModalProps) => {
  const { openModal, targetData, isEdit, setOpenModal, headerTitle, currentPage, queryClient, setCurrentPage } = props;
  const [t] = useTranslation();
  const rootRef = useRef(null);
  const formatsOptions = formatOptions;
  const [formatSelect, setFormatSelect] = useState<any>(null);
  const integrationsOptions = integrationOptions;
  const [integrationSelect, setIntegrationSelect] = useState<any>(null);
  const [isApiFormat, setIsApiFormat] = useState(false);

  const ValidateSchema = Yup.object().shape({
    name: Yup.string().nullable().notRequired(),
    description: Yup.string().nullable().notRequired(),
    integration: Yup.string().nullable().notRequired(),
    format: Yup.string().nullable().notRequired(),
    url: Yup.string()
      .nullable()
      .notRequired()
      .matches(urlRegex, { excludeEmptyString: true, message: t('errors.urlFormat') }),
    remitEmail: Yup.string().nullable().notRequired(),
  });

  const hookForm = useForm({
    mode: "onChange",
    resolver: yupResolver(ValidateSchema)
  });

  const {
    handleSubmit,
    setValue,
    reset,
    trigger
  } = hookForm;

  useEffect(() => {
    if (isEdit) {
      setValue('name', targetData?.name);
      setValue('description', targetData?.description);
      setValue('remitEmail', targetData?.remitEmail);
      setValue('format', targetData?.format);
      setFormatSelect(formatsOptions?.find((x: any) => x.value === targetData?.format));
      setValue('integration', targetData?.integration);
      setIntegrationSelect(integrationsOptions?.find((x: any) => x.value === targetData?.integration));
      setIsApiFormat(targetData?.format === 'API');
      setValue('organizationId', targetData.organizationId);
      setValue('url', targetData?.url);
      reset(targetData);
    } else {
      reset();
    }

    // eslint-disable-next-line
  }, []);

  const handleAddAndUpdateVendor = async (payload: updateOrgVendorDetailPayload) => {
    if (isEdit) {
      const nextPayload = { ...payload };
      if (nextPayload.format == null) delete nextPayload.format;
      if (nextPayload.url == null) delete nextPayload.url;
      if (nextPayload.integration == null) delete nextPayload.integration;
      if (nextPayload.remitEmail == null) delete nextPayload.remitEmail;
      await updateOrgVendorDetail(targetData.id, nextPayload);
    }
  };

  const handleChangeFormat = (option: any) => {
    setFormatSelect(option);
    setValue('format', option.value);
    setIsApiFormat(option?.value === 'API');
};

const handleChangeIntegration = (option: any) => {
  setIntegrationSelect(option);
  setValue('integration', option?.value);
  trigger('integration');
};

  const mutation = useMutation(['update-org-vendor-detail'], { mutationFn: handleAddAndUpdateVendor });

  const onSubmit = (values: any, props: any) => {
    console.log('values', values);
    mutation.mutate(values, {
      onSuccess: () => {
        toggleHandler();
        if (currentPage !== 0) {
          queryClient.invalidateQueries('getOrgVendors', { refetchActive: false }, { cancelRefetch: true });
          setCurrentPage(0);
        } else queryClient.invalidateQueries('getOrgVendors', { refetchActive: true }, { cancelRefetch: true });
        const message: string = t('vendorPage.editSuccess');
        toast.success(message);
      },
      onError: error => {
        const message: string = messageErrors(error, t);
        toast.error(message);
      },
    });
  };

  const toggleHandler = () => {
    setOpenModal(!openModal);
    reset();
  };

  return (
    <div ref={rootRef} className="vendor-modal-container">
      <Modal
        show={openModal}
        size="3xl"
        className="vendor-modal"
        root={rootRef.current ?? undefined}
        onClose={toggleHandler}
        dismissible={true}
        theme={themModal}
      >
        <CustomModalHeader title={headerTitle} toggle={toggleHandler} />
        <Modal.Body theme={{ base: 'flex-12 pb-4 pt-3 px-12' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="my-4 relative">
                <InputText 
                hookForm={hookForm}
                placeholder={`${t('vendorPage.modal.name')}`}
                name="name"
                id="name"
                isHolderLabel={true}
                disabled={true}
                />
            </div>
            <div className="my-4 relative">
                <InputText 
                hookForm={hookForm}
                placeholder={`${t('vendorPage.modal.description')}`}
                name="description"
                id="description"
                isHolderLabel={true}
                disabled={true}
                multiLine={true}
                />
            </div>
            <div className="mt-2 relative flex md:flex-row flex-col">
                              <InputText 
                                hookForm={hookForm} 
                                placeholder={`${t('vendorPage.modal.email')}`}
                                name="remitEmail" 
                                id="remitEmail" 
                                className="md:mr-4 mb-2 md:mb-0" 
                                isHolderLabel={true}
                            />
                            <MultiSelect
                                className="mb-2 md:mb-0"
                                onChange={handleChangeFormat}
                                options={formatsOptions}
                                value={formatSelect}
                                hookForm={hookForm}
                                name="format"
                                id="format"
                                noOptionsMessage={() => t('No options')}
                                closeMenuOnSelect
                                isHolderLabel={true}
                                placeholder={`${t('vendorPage.modal.format')}`}
                            />
                        </div>
            <div className="my-4 relative">
              <MultiSelect
                className="mb-2 md:mb-0"
                onChange={handleChangeIntegration}
                options={integrationsOptions}
                value={integrationSelect}
                hookForm={hookForm}
                name="integration"
                id="integration"
                noOptionsMessage={() => t('No options')}
                closeMenuOnSelect
                isHolderLabel={true}
                placeholder={`${t('vendorPage.modal.integration')}`}
              />
            </div>
            {isApiFormat && (<div className="my-4 relative">
                <InputText 
                  hookForm={hookForm} 
                  placeholder={`${t('vendorPage.modal.url')}`}
                  name="url" 
                  id="url" 
                  className="mb-2 md:mb-0"
                  isHolderLabel={true}
                />
            </div>)
            }
            <GroupButton
              className="items-center justify-center pt-5 mb-4"
              buttons={[
                {
                  type: 'button',
                  text: t('modal.cancel'),
                  classType: 'white',
                  action: () => toggleHandler(),
                },
                {
                  type: 'submit',
                  text: t('modal.save'),
                  classType: 'blue',
                  isLoading: mutation.isLoading,
                },
              ]}
            />
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};
export default VendorModal;
